import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useSession } from '../session-context';
import { useToast } from '../@/components/ui/use-toast';

const JoinSession: React.FC = () => {
  const [sessionIdInput, setSessionIdInput] = useState('');
  const [userName, setUserNameInput] = useState('');
  const [joinAsDM, setJoinAsDM] = useState(false);
  const { joinSession, createSession, setUserName } = useSession();
  const { toast } = useToast();
  const [isJoining, setIsJoining] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const handleJoinSession = async () => {
    if (!sessionIdInput.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a session ID',
        variant: 'destructive',
      });
      return;
    }

    if (userName.trim()) {
      setUserName(userName.trim());
    }

    setIsJoining(true);
    try {
      const success = await joinSession(sessionIdInput.trim(), joinAsDM);
      if (!success) {
        toast({
          title: 'Error',
          description: 'Session not found or invalid',
          variant: 'destructive',
        });
      } else if (joinAsDM) {
        toast({
          title: 'DM Role',
          description: 'You have joined as the Dungeon Master',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to join session',
        variant: 'destructive',
      });
    } finally {
      setIsJoining(false);
    }
  };

  const handleCreateSession = async () => {
    if (userName.trim()) {
      setUserName(userName.trim());
    }

    setIsCreating(true);
    try {
      await createSession(joinAsDM);
      if (joinAsDM) {
        toast({
          title: 'DM Role',
          description: 'You have created a session as the Dungeon Master',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to create session',
        variant: 'destructive',
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto mt-8">
      <CardHeader>
        <CardTitle>Stormwrack Campaign Session</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="userName">Your Name</Label>
            <Input
              id="userName"
              value={userName}
              onChange={(e) => setUserNameInput(e.target.value)}
              placeholder="Enter your display name"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="sessionId">Session ID</Label>
            <Input
              id="sessionId"
              value={sessionIdInput}
              onChange={(e) => setSessionIdInput(e.target.value)}
              placeholder="Enter a session ID to join"
            />
          </div>
          <div className="flex items-center space-x-2 mt-2">
            <Checkbox 
              id="joinAsDM" 
              checked={joinAsDM} 
              onCheckedChange={(checked) => setJoinAsDM(checked === true)}
            />
            <Label htmlFor="joinAsDM" className="cursor-pointer">Join as Dungeon Master</Label>
          </div>
          <div className="flex flex-col space-y-2">
            <Button 
              onClick={handleJoinSession} 
              disabled={isJoining || isCreating}
            >
              {isJoining ? 'Joining...' : 'Join Session'}
            </Button>
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">Or</span>
              </div>
            </div>
            <Button 
              onClick={handleCreateSession} 
              variant="outline"
              disabled={isJoining || isCreating}
            >
              {isCreating ? 'Creating...' : 'Create New Session'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default JoinSession;